export type SegmentedTabOption<T extends string = string> = {
    value: T;
    label: string;
    disabled?: boolean;
    title?: string;
};

export type SegmentedTabSwitchProps<T extends string> = {
    value: T;
    onChange: (value: T) => void;
    options: readonly [SegmentedTabOption<T>, SegmentedTabOption<T>];
    /** Ex.: "Filtrar listas" — `aria-label` do tablist. */
    ariaLabel: string;
    className?: string;
    /** Se `false`, mantém capitalização do `label` (ex.: "Minhas listas"). */
    uppercase?: boolean;
};

export function SegmentedTabSwitch<T extends string>({
    value,
    onChange,
    options,
    ariaLabel,
    className = '',
    uppercase = true,
}: SegmentedTabSwitchProps<T>) {
    const [a, b] = options;

    const textTone = uppercase ? 'text-xs font-bold uppercase tracking-wide' : 'text-sm font-semibold tracking-wide';

    const tabBtn = (opt: SegmentedTabOption<T>) => {
        const selected = value === opt.value;
        return (
            <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => {
                    if (opt.disabled) return;
                    onChange(opt.value);
                }}
                disabled={opt.disabled}
                title={opt.title}
                className={`flex-1 rounded-xl px-3 py-2.5 transition-all sm:px-4 ${textTone} ${
                    selected
                        ? 'bg-orange-600 text-white shadow-lg shadow-orange-900/30'
                        : 'text-white/45 hover:bg-white/[0.06] hover:text-white/80'
                } ${opt.disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
            >
                {opt.label}
            </button>
        );
    };

    return (
        <div
            className={`flex w-full rounded-2xl border border-white/10 bg-white/[0.03] p-1 ${className}`.trim()}
            role="tablist"
            aria-label={ariaLabel}
        >
            {tabBtn(a)}
            {tabBtn(b)}
        </div>
    );
}
